import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ButtonComponent,
  CheckboxComponent,
  InputComponent,
  RadioButtonComponent,
  DropdownComponent,
} from "../../atoms";
import _, { set } from "lodash";
import AddressCard from "./AddressCard";
import { getChecked } from "../../../utils/Credentials";
import { IMAGES } from "../../../assets";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { toast, ToastContainer } from "react-toastify";
import { DashBoardRepository } from "../../../repository";

export const ShippingDetailsCard = ({
  shippingCost = 0,
  shippingCharge = 0,
  otherOptions = [],
  paymentMethods = [],
  handleAdditionalCost = () => { },
  onChangePromo = () => { },
  check,
  setCheck = () => { },
  promoValue,
  onClickCheck = () => { },
  isDiscount = false,
  promoCodeError = false,
  promoCodeErrorMessage = "",
  promotion = {},
  radioButton,
  handleChangeRadio = () => { },
  lateFee = 0,
  lateDetails = [],
  isCheckDisabled = false,
  handleShipmentSubmit = () => { },
  shipBtnName = "",
  shipBtnIsDisabled = false,
  addressList = [],
  onChangeAddress = () => { },
  addressId,
  addressDetails = [],
  checkTerms,
  handleChangeTermsCheckbox = () => { },
  selectedOtherCharges = [],
  packageId,
}) => {
  const [isReturnOrDisposeSelected, setIsReturnOrDisposeSelected] =
    useState(false);
  const [isReturnSelected, setIsReturnSelected] = useState(false);
  const [isDisposeSelected, setIsDisposeSelected] = useState(false);
  const [fileName, setFileName] = useState("");
  const [returnSubOption, setReturnSubOption] = useState(""); // 'vendor' | 'relation'
  const [relationAddress, setRelationAddress] = useState({
    Address1: "",
    Address2: "",
    City: "",
    State: "",
    Country: "USA",
    PostalCode: "",
    TelephoneNo: "+1",
    ConsigneeFirstName: "",
    ConsigneeMiddleName: "",
    ConsigneeLastName: "",
  });
  const [relationAddressErrors, setRelationAddressErrors] = useState({});

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedMimeTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/gif",
    ];

    if (!allowedMimeTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload a PDF or supported image.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Max 5MB.");
      return;
    }

    if (!packageId) {
      toast.error("Package ID is missing. Please refresh and try again.");
      return;
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const byteArray = new Uint8Array(arrayBuffer);
      const base64String = btoa(
        byteArray.reduce((data, byte) => data + String.fromCharCode(byte), ""),
      );

      const params = {
        packageId,
        fileName: file.name,
        documentBase64: base64String,
        contentType: file.type,
      };

      const response = await DashBoardRepository.uploadReturnLabel(params);

      if (response?.status_code == 1) {
        setFileName(file.name);
        toast.success(
          response?.message || "Return label uploaded successfully.",
        );
      } else {
        toast.error(response?.message || "Failed to upload return label.");
      }
    } catch (error) {
      toast.error("Failed to process file. Please try again.");
    }
  };

  useEffect(() => {
    const selectedCode = selectedOtherCharges?.[0]?.code;

    setIsReturnSelected(selectedCode === "ReturnPackage");
    setIsDisposeSelected(selectedCode === "DisposePackage");
    setIsReturnOrDisposeSelected(
      selectedCode === "DisposePackage" || selectedCode === "ReturnPackage",
    );
  }, [selectedOtherCharges]);

  const roundToDecimalPlace = (number, decimalPlaces) => {
    const factor = Math.pow(10, decimalPlaces);
    return Math.round(number * factor) / factor;
  };

  const getLateDaysSum = () => {
    const totalLateDays = lateDetails.reduce((sum, item) => {
      return sum + (item.lateDays || 0); // Add lateDays if it exists, otherwise add 0
    }, 0);
    return totalLateDays;
  };

  function formatString(input) {
    return input
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
  }

  const isSpecialSelected = selectedOtherCharges?.some(
    (c) => c.code === "DisposePackage" || c.code === "ReturnPackage",
  );

  const validateRelationAddress = () => {
    const required = [
      "Address1",
      "Address2",
      "City",
      "State",
      "PostalCode",
      "TelephoneNo",
      "ConsigneeFirstName",
      "ConsigneeLastName",
    ];
    const errors = {};
    required.forEach((field) => {
      if (!relationAddress[field] || !relationAddress[field].trim()) {
        errors[field] = "This field is required";
      }
    });

    // Phone validation - must be exactly 10 digits after +1
    const digitsAfterPlus1 = relationAddress.TelephoneNo.replace(/^\+1/, '').replace(/\D/g, '');
    if (digitsAfterPlus1.length !== 10) {
      errors.TelephoneNo = "Phone number must be exactly 10 digits";
    }

    return errors;
  };

  const onClickSubmit = () => {
    if (isReturnSelected) {
      if (!returnSubOption) {
        toast.error("Please select a return option (Vendor or Relation).");
        return;
      }
      if (returnSubOption === "vendor" && !fileName) {
        toast.error("Please upload return label before continuing.");
        return;
      }
      if (returnSubOption === "relation") {
        const errors = validateRelationAddress();
        if (Object.keys(errors).length > 0) {
          setRelationAddressErrors(errors);
          return;
        }
        setRelationAddressErrors({});
      }
    }

    handleShipmentSubmit({
      returnSubOption: isReturnSelected ? returnSubOption : null,
      relationAddress:
        isReturnSelected && returnSubOption === "relation"
          ? relationAddress
          : null,
    });
  };

  return (
    <Card sx={{ p: 1 }}>
      <ToastContainer />
      <Box>
        <Typography
          textAlign={"center"}
          fontFamily={"Source Sans Pro"}
          fontWeight={600}
          variant="subtitle1"
        >
          Shipping Details & Options
        </Typography>
      </Box>
      <Divider />
      <Container>
        <Stack spacing={2} p={1}>
          <Box>
            <Box>
              <Typography
                fontWeight={600}
                fontFamily={"Source Sans Pro"}
                variant="subtitle1"
              >
                Shipping Method & Cost
              </Typography>
            </Box>
            <Grid container alignItems={"center"}>
              <Grid item xs={8}>
                <Box>
                  <RadioButtonComponent
                    selectedValue={"fed_ex"}
                    value={"fed_ex"}
                    name={"shippingMethod"}
                    label={"FedExEconomy"}
                    disableRipple={true}
                  />
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box>
                  <Typography
                    textAlign={"right"}
                    fontWeight={600}
                    fontSize={14}
                    fontFamily={"Source Sans Pro"}
                    variant="subtitle1"
                  >
                    Currently Unavailable
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={8}>
                <Box>
                  <RadioButtonComponent
                    selectedValue={"fed_ex"}
                    value={"fed_ex"}
                    name={"shippingMethod"}
                    label={"FedExPriority"}
                    disableRipple={isSpecialSelected}

                  />
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box>
                  <Typography
                    textAlign={"right"}
                    fontWeight={600}
                    fontSize={14}
                    fontFamily={"Source Sans Pro"}
                    variant="subtitle1"
                  >
                    ${roundToDecimalPlace(shippingCharge, 2)}
                  </Typography>
                </Box>
              </Grid>
              {/* {lateFee > 0 ? (<>
              <Grid item xs={8}>
              <Box>
                <RadioButtonComponent
                  label={`Late Charges - For ${getLateDaysSum()} days`}
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box>
                <Typography
                  textAlign={"right"}
                  fontWeight={600}
                  fontSize={14}
                  fontFamily={"Source Sans Pro"}
                  variant="subtitle1"
                >
                  ${roundToDecimalPlace(lateFee, 2)} 
                </Typography>
              </Box>
            </Grid>
            </>):''} */}

              {/* {!isReturnOrDisposeSelected && (
                <>
                  <Grid item xs={8}>
                    <Box>
                      <RadioButtonComponent
                        selectedValue={"fed_ex"}
                        value={"fed_ex"}
                        name={"shippingMethod"}
                        label={"FedExEconomy"}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <Typography
                        textAlign={"right"}
                        fontWeight={600}
                        fontSize={14}
                        fontFamily={"Source Sans Pro"}
                        variant="subtitle1"
                      >
                        ${roundToDecimalPlace(shippingCharge, 2)}
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )} */}
              {lateFee > 0 ? (
                <>
                  <Grid item xs={8}>
                    <Box>
                      <RadioButtonComponent
                        label={`Late Charges - For ${getLateDaysSum()} days`}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <Typography
                        textAlign={"right"}
                        fontWeight={600}
                        fontSize={14}
                        fontFamily={"Source Sans Pro"}
                        variant="subtitle1"
                      >
                        ${roundToDecimalPlace(lateFee, 2)}
                      </Typography>
                    </Box>
                  </Grid>
                </>
              ) : (
                ""
              )}
            </Grid>
          </Box>
          {_.map(otherOptions, (item, index) => {
            return (
              <Box key={index + 1}>
                <Box>
                  <Typography
                    fontWeight={600}
                    fontFamily={"Source Sans Pro"}
                    variant="subtitle1"
                  >
                    {formatString(_.get(item, "charge_group"))}
                  </Typography>
                </Box>
                {_.map(_.get(item, "charge_list"), (charge, index) => {
                  const selectedCode = selectedOtherCharges?.[0]?.code;
                  const isDisposeSelected = selectedCode === "DisposePackage";
                  const isReturnSelected = selectedCode === "ReturnPackage";
                  const isCurrentDispose =
                    charge.myus_parameter === "DisposePackage";

                  const isCurrentReturn =
                    charge.myus_parameter === "ReturnPackage";
                  return (
                    <Grid container alignItems={"center"} key={index + 1}>
                      <Grid item xs={8}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CheckboxComponent
                            value={check ? 0 : 1}
                            disabled={
                              isDisposeSelected
                                ? !isCurrentDispose
                                : isReturnSelected
                                  ? !isCurrentReturn
                                  : false
                            }
                            onChange={(e) =>
                              handleAdditionalCost(e.target.checked, {
                                description: _.get(charge, "title"),
                                charge: _.get(charge, "cost"),
                                code: _.get(charge, "myus_parameter"),
                              })
                            }
                          />
                          <Typography
                            variant="subtitle1"
                            fontFamily={"Source Sans Pro"}
                          >
                            {_.get(charge, "title")}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box>
                          <Typography
                            textAlign={"right"}
                            fontWeight={600}
                            fontFamily={"Source Sans Pro"}
                            variant="subtitle1"
                          >
                            ${_.get(charge, "cost")}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  );
                })}
              </Box>
            );
          })}

          {/* <Box>
            <Box>
              <Typography
                fontWeight={600}
                fontFamily={"Source Sans Pro"}
                variant="subtitle1"
              >
                Payment Method
              </Typography>
            </Box>
            <Grid container alignItems={"center"}>
              <Grid item xs={8}>
                {_.map(paymentMethods, (item, index) => {
                  return (
                    <Box key={index + 1}>
                      <RadioButtonComponent
                        name={"payment"}
                        selectedValue={radioButton}
                        value={_.get(item, "id")}
                        label={_.get(item, "slug")}
                        image={_.get(item, "image")}
                        handleChange={() => handleChangeRadio(_.get(item, "id"))}
                      />
                    </Box>
                  );
                })}
              </Grid>
            </Grid>
          </Box> */}
          <Divider />

          <Box>
            <Box>
              <Typography
                fontWeight={600}
                fontFamily={"Source Sans Pro"}
                variant="subtitle1"
              >
                Check Discount
              </Typography>
            </Box>
            <Grid
              container
              sx={{ display: "flex", alignItems: "flex-start" }}
              spacing={1}
              pt={1}
            >
              <Grid item xs={8}>
                <InputComponent
                  isError={promoCodeError}
                  error={promoCodeErrorMessage}
                  pro
                  label={"Promo Code"}
                  value={promoValue}
                  onChange={onChangePromo}
                  isDisabled={isCheckDisabled}
                />
              </Grid>

              <Grid item xs={4}>
                <ButtonComponent
                  name={"Check"}
                  variant="contained"
                  onClick={onClickCheck}
                  disabled={isCheckDisabled}
                />
              </Grid>
            </Grid>
          </Box>
          {isDiscount ? (
            <Box sx={{ display: "flex" }}>
              {_.get(promotion, "type_name") == "percentage" ? (
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  fontFamily={"Source Sans Pro"}
                >
                  You have recived a discount of {_.get(promotion, "value")}%
                </Typography>
              ) : null}

              {_.get(promotion, "type_name") == "flat" ? (
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  fontFamily={"Source Sans Pro"}
                >
                  You have recived a discount of ${_.get(promotion, "value")}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          <Box
            sx={{
              borderStyle: "solid",
              borderWidth: 1,
              borderColor: "#B3B3B3",
              borderRadius: 2,
              bgcolor: "white",
              p: 1,
            }}
          >
            <Typography
              fontFamily={"Source Sans Pro"}
              variant="subtitle1"
              textAlign={"center"}
            >
              Estimated Shipping cost
            </Typography>
            <Typography
              fontFamily={"Source Sans Pro"}
              variant="subtitle1"
              textAlign={"center"}
            >
              ${roundToDecimalPlace(shippingCost, 2)}
            </Typography>
          </Box>

          {!isReturnOrDisposeSelected ? (
            <Box>
              <DropdownComponent
                label="Shipping To"
                value={addressId}
                dropdownList={addressList}
                disabled={false}
                onChange={onChangeAddress}
              />
              {addressId > -1 && (
                <>
                  <br />
                  <AddressCard
                    item={addressDetails}
                    showBackBtn={false}
                    showRadio={false}
                    isEditable={false}
                  />
                </>
              )}
            </Box>
          ) : (
            isReturnSelected && (
              <Box sx={{ mt: 1 }}>
                {/* Return sub-option selection */}
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  fontFamily="Source Sans Pro"
                  sx={{ mb: 1 }}
                >
                  Select Return Type
                </Typography>
                <RadioButtonComponent
                  label="Return to Vendor"
                  value="vendor"
                  selectedValue={returnSubOption}
                  handleChange={() => {
                    setReturnSubOption("vendor");
                    setRelationAddressErrors({});
                  }}
                />
                <RadioButtonComponent
                  label="Return to Relation"
                  value="relation"
                  selectedValue={returnSubOption}
                  handleChange={() => {
                    setReturnSubOption("relation");
                    setFileName("");
                  }}
                />

                {/* Vendor: file upload */}
                {returnSubOption === "vendor" && (
                  <Box
                    sx={{
                      my: 2,
                      p: 2,
                      border: "2px dashed #7919d2",
                      borderRadius: 2,
                      textAlign: "center",
                      bgcolor: "#f9f9f9",
                      transition: "0.3s",
                      "&:hover": {
                        backgroundColor: "#e3f2fd",
                        cursor: "pointer",
                      },
                    }}
                    onClick={() =>
                      document.getElementById("file-upload-input").click()
                    }
                  >
                    <UploadFileIcon
                      sx={{ fontSize: 40, color: "#7919d2", mb: 1 }}
                    />
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      fontFamily="Source Sans Pro"
                    >
                      Upload return address label (PDF or Image)
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, color: "#892a96" }}
                    >
                      Max file size: 5MB
                    </Typography>
                    <input
                      id="file-upload-input"
                      type="file"
                      accept="application/pdf, image/jpeg, image/png, image/webp, image/gif"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                    {fileName && (
                      <Typography
                        variant="body2"
                        sx={{ mt: 1, fontWeight: 500, color: "#7919d2" }}
                      >
                        Selected file: {fileName} (click to change)
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Relation: address form */}
                {returnSubOption === "relation" && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                      bgcolor: "#fafafa",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      fontFamily="Source Sans Pro"
                      sx={{ mb: 1.5, color: "#7919d2" }}
                    >
                      Relation's US Address
                    </Typography>
                    <Stack spacing={1.5}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <InputComponent
                            label="First Name *"
                            value={relationAddress.ConsigneeFirstName}
                            onChange={(e) => {
                              setRelationAddress((prev) => ({
                                ...prev,
                                ConsigneeFirstName: e.target.value,
                              }));
                              if (e.target.value.trim())
                                setRelationAddressErrors((prev) => ({
                                  ...prev,
                                  ConsigneeFirstName: "",
                                }));
                            }}
                            isError={!!relationAddressErrors.ConsigneeFirstName}
                            error={relationAddressErrors.ConsigneeFirstName}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <InputComponent
                            label="Middle Name"
                            value={relationAddress.ConsigneeMiddleName}
                            onChange={(e) =>
                              setRelationAddress((prev) => ({
                                ...prev,
                                ConsigneeMiddleName: e.target.value,
                              }))
                            }
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <InputComponent
                            label="Last Name *"
                            value={relationAddress.ConsigneeLastName}
                            onChange={(e) => {
                              setRelationAddress((prev) => ({
                                ...prev,
                                ConsigneeLastName: e.target.value,
                              }));
                              if (e.target.value.trim())
                                setRelationAddressErrors((prev) => ({
                                  ...prev,
                                  ConsigneeLastName: "",
                                }));
                            }}
                            isError={!!relationAddressErrors.ConsigneeLastName}
                            error={relationAddressErrors.ConsigneeLastName}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <InputComponent
                            label="Telephone No *"
                            value={relationAddress.TelephoneNo}
                            onChange={(e) => {
                              const value = e.target.value;

                              // Always start with +1
                              let newValue = value;
                              if (!value.startsWith('+1')) {
                                newValue = '+1' + value.replace(/^\+1/, '');
                              }

                              setRelationAddress((prev) => ({
                                ...prev,
                                TelephoneNo: newValue,
                              }));

                              // Check for exactly 10 digits after +1
                              const digitsAfterPlus1 = newValue.replace(/^\+1/, '').replace(/\D/g, '');
                              if (digitsAfterPlus1.length === 10) {
                                setRelationAddressErrors((prev) => ({
                                  ...prev,
                                  TelephoneNo: "",
                                }));
                              } else {
                                setRelationAddressErrors((prev) => ({
                                  ...prev,
                                  TelephoneNo: "Phone number must be exactly 10 digits",
                                }));
                              }
                            }}
                            isError={!!relationAddressErrors.TelephoneNo}
                            error={relationAddressErrors.TelephoneNo}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <InputComponent
                            label="Address Line 1 *"
                            value={relationAddress.Address1}
                            onChange={(e) => {
                              setRelationAddress((prev) => ({
                                ...prev,
                                Address1: e.target.value,
                              }));
                              if (e.target.value.trim())
                                setRelationAddressErrors((prev) => ({
                                  ...prev,
                                  Address1: "",
                                }));
                            }}
                            isError={!!relationAddressErrors.Address1}
                            error={relationAddressErrors.Address1}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <InputComponent
                            label="Address Line 2 *"
                            value={relationAddress.Address2}
                            onChange={(e) => {
                              setRelationAddress((prev) => ({
                                ...prev,
                                Address2: e.target.value,
                              }));
                              if (e.target.value.trim())
                                setRelationAddressErrors((prev) => ({
                                  ...prev,
                                  Address2: "",
                                }));
                            }}
                            isError={!!relationAddressErrors.Address2}
                            error={relationAddressErrors.Address2}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <InputComponent
                            label="City *"
                            value={relationAddress.City}
                            onChange={(e) => {
                              setRelationAddress((prev) => ({
                                ...prev,
                                City: e.target.value,
                              }));
                              if (e.target.value.trim())
                                setRelationAddressErrors((prev) => ({
                                  ...prev,
                                  City: "",
                                }));
                            }}
                            isError={!!relationAddressErrors.City}
                            error={relationAddressErrors.City}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <InputComponent
                            label="State *"
                            value={relationAddress.State}
                            onChange={(e) => {
                              setRelationAddress((prev) => ({
                                ...prev,
                                State: e.target.value,
                              }));
                              if (e.target.value.trim())
                                setRelationAddressErrors((prev) => ({
                                  ...prev,
                                  State: "",
                                }));
                            }}
                            isError={!!relationAddressErrors.State}
                            error={relationAddressErrors.State}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <InputComponent
                            label="Zip Code *"
                            value={relationAddress.PostalCode}
                            onChange={(e) => {
                              setRelationAddress((prev) => ({
                                ...prev,
                                PostalCode: e.target.value,
                              }));
                              if (e.target.value.trim())
                                setRelationAddressErrors((prev) => ({
                                  ...prev,
                                  PostalCode: "",
                                }));
                            }}
                            isError={!!relationAddressErrors.PostalCode}
                            error={relationAddressErrors.PostalCode}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <InputComponent
                            label="Country"
                            value="USA"
                            isDisabled={true}
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </Box>
                )}
              </Box>
            )
          )}
          <Box>
            <Grid item>
              <Grid container alignItems={"center"}>
                <Grid item>
                  <CheckboxComponent
                    checked={checkTerms}
                    inputProps={{ "aria-label": "controlled" }}
                    onChange={handleChangeTermsCheckbox}
                  />
                </Grid>
                <Grid item>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: useTheme().palette.grey[500],
                      fontFamily: "Source sans pro",
                      fontWeight: 600,
                    }}
                  >
                    I agree to the{" "}
                    <a
                      href="/terms-and-conditions"
                      target="_blank"
                      style={{ textDecoration: "none" }}
                    >
                      {" "}
                      <span className="hightlighted-text">
                        Terms & Conditions
                      </span>
                    </a>
                    .
                  </Typography>
                </Grid>
              </Grid>
              <Grid item>
                <img src={IMAGES?.LOGO?.PAYMENT_SOLUTIONS} width="100%" />
              </Grid>
            </Grid>
          </Box>
          <Box>
            <ButtonComponent
              size="large"
              variant="contained"
              name={isDisposeSelected && shippingCost === 0 ? "Dispose Package" : shipBtnName}
              width={"100%"}
              onClick={onClickSubmit}
              disabled={shipBtnIsDisabled}
            />
          </Box>
        </Stack>
      </Container>
    </Card>
  );
};
